const STORAGE_KEY = "pendingJoinCode";

/**
 * How long an invite code survives an interrupted sign-in.
 *
 * The code is written before an OAuth redirect and read when the user lands
 * back. That round trip can include picking a Google account, a password
 * prompt, and 2FA, so it needs generous headroom — but a code left behind for
 * days would silently attach an unrelated later login to someone's invite.
 */
const TTL_MS = 60 * 60 * 1000; // 1 hour

/** Remember the invite code to accept once the user comes back authenticated. */
export function rememberPendingJoinCode(code: string): void {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ code, savedAt: Date.now() }),
  );
}

/**
 * Read the pending invite code without consuming it.
 *
 * Deliberately *not* a take-and-clear like `takePendingTemplate`: the caller
 * has to POST the code to the backend, and clearing first means a failed
 * request loses the invite for good — the user then gets asked for their code
 * all over again. Callers clear with `clearPendingJoinCode()` once the accept
 * has actually succeeded; until then the TTL bounds any retrying.
 *
 * Entries that are expired or malformed are cleared here and read as null, so
 * they can't be re-evaluated on every future login.
 */
export function readPendingJoinCode(): string | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  const drop = () => {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  };

  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return drop();
    const { code, savedAt } = parsed as { code?: unknown; savedAt?: unknown };
    if (typeof code !== "string" || typeof savedAt !== "number") return drop();
    return Date.now() - savedAt > TTL_MS ? drop() : code;
  } catch {
    // A bare code written by a build from before this change. It still
    // identifies a real invite and we can't know its age, so honour it once —
    // the caller clears it on success.
    return raw;
  }
}

/** Clear the pending code — call once the invite has been accepted. */
export function clearPendingJoinCode(): void {
  localStorage.removeItem(STORAGE_KEY);
}
